import { authHeaders } from "../api/rest";
import { readSse } from "./sse";

const BASE = import.meta.env.VITE_API_BASE ?? "";

export interface AssistantStatus { configured: boolean; model: string }

export interface ChatTurn { role: "user" | "assistant"; content: string }

export interface AlertContext { code: string; severity: string; subject?: string | null; ts: string; assetId?: string | null }

/** Where the user is — the second system block on the backend (ADR 379). */
export interface ChatContext {
  page: string;
  warehouseId?: string;
  runId?: string;
  item?: string;
  locale: string;
  alerts: AlertContext[];
  liveCount?: Record<string, unknown>;
}

/** The provenance the question API attaches to every answer (ADR 376). */
export interface Provenance {
  runId?: string | null; runName?: string | null; runStatus?: string | null; runCreatedAt?: string | null;
  warehouseId?: string | null; snapshotAsOf?: string | null;
  window?: { from: string; to: string; windowDays: number; coveredDays: number; importIds?: string[] } | null;
  definition?: string; decisions?: number[]; link?: string;
}

export type ChatEvent =
  | { event: "tool"; name: string; args: Record<string, unknown> }
  | { event: "answer"; name: string; provenance?: Provenance; error?: string }
  | { event: "text"; text: string; truncated: boolean }
  | { event: "done"; inputTokens: number; outputTokens: number; cacheReadTokens: number; provenances: Provenance[] }
  | { event: "error"; status: number; message: string };

export async function getAssistantStatus(siteId: string): Promise<AssistantStatus> {
  const res = await fetch(`${BASE}/v1/sites/${encodeURIComponent(siteId)}/assistant/status`, {
    headers: authHeaders(), credentials: "include",
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return (await res.json()) as AssistantStatus;
}

/** One reply, streamed. Resolves when the stream ends; every event goes to `onEvent` as it arrives. */
export async function streamAssistantChat(
  siteId: string, messages: ChatTurn[], context: ChatContext, onEvent: (e: ChatEvent) => void, signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${BASE}/v1/sites/${encodeURIComponent(siteId)}/assistant/chat`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json", Accept: "text/event-stream" }),
    credentials: "include",
    body: JSON.stringify({ messages, context }),
    signal,
  });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try { const j = await res.json(); if (j?.error) message = String(j.error); } catch { /* not JSON */ }
    onEvent({ event: "error", status: res.status, message });
    return;
  }
  await readSse(res, (e) => {
    try {
      onEvent({ event: e.event, ...(JSON.parse(e.data) as object) } as ChatEvent);
    } catch {
      onEvent({ event: "error", status: 0, message: "unreadable event" });
    }
  });
}
